import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { 
  InformationCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/solid';
import { statusLabels } from '../config/statusColors';

const AdminStatusModal = ({ isOpen, onClose, currentStatus, onConfirm }) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const statuses = [
    { id: 'received', icon: InformationCircleIcon, label: statusLabels.received, color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
    { id: 'review', icon: ClipboardDocumentCheckIcon, label: statusLabels.review, color: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
    { id: 'contract', icon: DocumentTextIcon, label: statusLabels.contract, color: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' },
    { id: 'completed', icon: CheckCircleIcon, label: statusLabels.completed, color: 'bg-green-100 text-green-700 hover:bg-green-200' },
    { id: 'cancelled', icon: XCircleIcon, label: statusLabels.cancelled, color: 'bg-red-100 text-red-700 hover:bg-red-200' }
  ];

  const handleConfirm = () => {
    if (!selectedStatus || selectedStatus === currentStatus) return;

    const statusData = {
      status: selectedStatus,
      status_log_type: 'admin_change',
      status_log_message: `관리자가 상태를 ${statusLabels[selectedStatus]}(으)로 변경했습니다.`,
      status_user_memo: null, // 관리자 직접 변경은 사용자 메모 없음
      status_internal_memo: reason || null,
      status_is_public: false // 관리자 변경은 내부 기록으로
    };

    onConfirm(statusData);
    
    // 초기화
    setSelectedStatus(currentStatus);
    setReason('');
    onClose(); // 모달 닫기
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl z-[10000] flex flex-col max-h-[90vh]">
        {/* 헤더 - 고정 */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h3 className="text-lg font-semibold">
            상태 직접 변경 (관리자 전용)
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 콘텐츠 - 스크롤 가능 */}
        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-4">
            프로젝트 상태를 직접 선택하여 변경할 수 있습니다.
          </p>

          {/* 상태 선택 */}
          <div className="space-y-2 mb-4">
            {statuses.map((status) => {
              const Icon = status.icon;
              const isSelected = selectedStatus === status.id;
              const isCurrent = currentStatus === status.id;
              
              return (
                <button
                  key={status.id}
                  onClick={() => setSelectedStatus(status.id)}
                  disabled={isCurrent}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50'
                      : isCurrent
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${status.color.split(' ')[0]}`}>
                    <Icon className={`h-5 w-5 ${status.color.split(' ')[1]}`} />
                  </div>
                  <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                    {status.label}
                  </span>
                  {isCurrent && (
                    <span className="ml-auto text-xs text-gray-500">현재 상태</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* 변경 사유 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              변경 사유 (선택사항)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="상태를 직접 변경하는 이유를 입력해주세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="3"
            />
          </div>

          {/* 경고 메시지 */}
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-yellow-800">
              <strong>주의:</strong> 정상적인 프로세스를 거치지 않고 상태를 변경합니다.
              변경 기록은 시스템에 남습니다.
            </p>
          </div>
        </div>

        {/* 푸터 - 고정 */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedStatus || selectedStatus === currentStatus}
            className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            변경하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminStatusModal;